/**
 * TimelockCountdown - Displays time remaining until timelock expires
 * 
 * Features:
 * - Live countdown timer
 * - Visual progress indicator
 * - Urgency coloring (green > yellow > red)
 * 
 * Part of #260 Governance Hub
 */

import { useState, useEffect } from 'react';
import './TimelockCountdown.css';

interface TimelockCountdownProps {
  endsAt: string;
  startedAt?: string;
  compact?: boolean;
  onComplete?: () => void;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

export default function TimelockCountdown({
  endsAt,
  startedAt,
  compact = false,
  onComplete,
}: TimelockCountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const endTime = new Date(endsAt).getTime();
      const now = Date.now();
      const diff = endTime - now;

      if (diff <= 0) {
        setIsComplete(true);
        setTimeRemaining(null);
        onComplete?.();
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds, total: diff });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [endsAt, onComplete]);

  // Calculate progress percentage if we have a start time
  const getProgress = () => {
    if (!startedAt || !timeRemaining) return 100;
    const startTime = new Date(startedAt).getTime();
    const endTime = new Date(endsAt).getTime();
    const now = Date.now();
    const totalDuration = endTime - startTime;
    const elapsed = now - startTime;
    return Math.min((elapsed / totalDuration) * 100, 100);
  };

  // Get urgency level based on time remaining
  const getUrgency = (): 'safe' | 'warning' | 'critical' => {
    if (!timeRemaining) return 'safe';
    const hoursLeft = timeRemaining.total / (1000 * 60 * 60);
    if (hoursLeft <= 1) return 'critical';
    if (hoursLeft <= 6) return 'warning';
    return 'safe';
  };

  const formatUnit = (value: number, unit: string) => {
    return `${value}${unit}`;
  };

  if (isComplete) {
    return (
      <div className={`timelock-countdown complete ${compact ? 'compact' : ''}`}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 4L6 11l-3-3" />
        </svg>
        <span>Timelock complete</span>
      </div>
    );
  }

  if (!timeRemaining) {
    return (
      <div className={`timelock-countdown loading ${compact ? 'compact' : ''}`}>
        <div className="spinner-small" />
      </div>
    );
  }

  const urgency = getUrgency();

  if (compact) {
    return (
      <div className={`timelock-countdown compact ${urgency}`}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="7" cy="7" r="6" />
          <path d="M7 4v3l2 1" />
        </svg>
        <span className="countdown-text">
          {timeRemaining.days > 0 && formatUnit(timeRemaining.days, 'd ')}
          {formatUnit(timeRemaining.hours, 'h ')}
          {formatUnit(timeRemaining.minutes, 'm')}
        </span>
      </div>
    );
  }

  const progress = getProgress();

  return (
    <div className={`timelock-countdown ${urgency}`}>
      {/* Progress bar */}
      {startedAt && (
        <div className="timelock-progress">
          <div 
            className={`progress-fill ${urgency}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Countdown display */}
      <div className="countdown-display">
        {timeRemaining.days > 0 && (
          <div className="countdown-unit">
            <span className="countdown-value">{timeRemaining.days}</span>
            <span className="countdown-label">days</span>
          </div>
        )}
        <div className="countdown-unit">
          <span className="countdown-value">{String(timeRemaining.hours).padStart(2, '0')}</span>
          <span className="countdown-label">hours</span>
        </div>
        <div className="countdown-separator">:</div>
        <div className="countdown-unit">
          <span className="countdown-value">{String(timeRemaining.minutes).padStart(2, '0')}</span>
          <span className="countdown-label">min</span>
        </div>
        <div className="countdown-separator">:</div>
        <div className="countdown-unit">
          <span className="countdown-value">{String(timeRemaining.seconds).padStart(2, '0')}</span>
          <span className="countdown-label">sec</span>
        </div>
      </div>

      {/* Status text */}
      <div className={`timelock-status ${urgency}`}>
        {urgency === 'critical' && (
          <span>Timelock expires soon!</span>
        )}
        {urgency === 'warning' && (
          <span>Timelock expiring</span>
        )}
        {urgency === 'safe' && (
          <span>Time remaining until execution</span>
        )}
      </div>
    </div>
  );
}
